// ============================================================================
// src/app/data/useRouteResource.ts — the route resource runtime, on TanStack
// ============================================================================
// B3.5. This is the swap the interim runtime was shaped for. Its header said:
//
//   "It is deliberately the SMALLEST runtime that is correct, because at B3.5
//    it is replaced by `useQuery` over the SAME key and the SAME queryFn."
//
// That is what happened, and nothing else moved: the key still comes from
// `resourceKey`, the query function is still called with (client, params,
// signal), the returned shape is still {data, loading, error, refetch}, and
// neither pilot surface changed a line. The hand-written effect — abort
// controller, key-stamped settles, reload token, promise resolvers — is gone,
// because Query owns all four.
//
// STILL NO CACHE. ON PURPOSE. See queryClient.js: gcTime is 0, so an entry dies
// with its last observer and a remount starts from nothing, exactly as before.
// This change moves the RUNTIME onto Query; it does not turn caching on. That
// is a separate change with its own invalidation contract to write.
//
// ---------------------------------------------------------------------------
// WHAT QUERY GIVES US FOR FREE, AND WHY IT MATTERS
//
// KEY IDENTITY. The interim runtime stamped every settle with the key it
// belonged to and dropped settles for superseded keys, so a late response for
// animal A could not paint into animal B's page. Query does not need the stamp:
// a settle is written to ITS OWN key's cache entry, which after a key change no
// observer is reading. The race is not fenced, it is structurally absent.
//
// A REFETCH THAT DOES NOT BLANK THE PAGE. This is the defect an adversarial
// review found in the interim runtime and it was live, deterministic, and
// destructive: `refetch` bumped a state token that was an effect dependency, so
// a same-key refresh took the KEY-CHANGE path and cleared data to null. The
// cattle record page full-screens its loading branch, so after EVERY field save
// the whole editor unmounted — anything typed into the next field during the
// save round trip was destroyed, focus fell to <body>, scroll reset. No error,
// no notice, on a 100-800ms window.
//
// The hand-written fix for it — a `dataKey` ref telling a key change apart from
// a same-key refresh — is PR #111, and it is already on main. An earlier version
// of this paragraph said the defect was still live here and that this branch
// forked before the fix; that was true when it was written at 891d2a50 and is
// false at this tip. `git merge-base main HEAD` is b01a2ca2, the PR #111 merge
// itself, so the version this file replaces already CONTAINS the hand-written
// fix.
//
// So the port does not introduce the fix and does not merely preserve it: it
// removes the MECHANISM the fix guards, where the hand-written version adds a
// branch to it. Anyone reconciling the two should know both remove the same
// defect by different means — and that the four PR #111 fingerprints going to
// zero here is the correct outcome, not a silent revert.
//
// Query cannot reproduce it. A refetch on an unchanged key keeps `data` and
// leaves isPending false; only a KEY CHANGE starts from nothing. The two cases
// the hand-written fix had to learn to tell apart are two different cache
// entries here, so the distinction is not a rule that can be forgotten.
//
// Note what is NOT used to achieve that: `placeholderData: keepPreviousData`.
// It is the usual answer for "keep showing the old data", and here it would be
// WRONG — it keeps the previous data across a KEY change, which is precisely
// the transient wrong-record paint the cattleId keying exists to prevent. A
// refresh may keep stale data on screen; a key change may not. Query's plain
// behaviour already draws the line in exactly the right place.
//
// A REFETCH THAT RETURNS SOMETHING. Seven call sites write `await loadAll()`.
// Query's refetch returns a promise that settles with the refetch.
//
// A CORRECT FIRST FRAME. With no scope yet, the query is parked disabled and
// reports pending — so a record page never gets a not-loading/not-found frame
// over an identity that simply had not resolved.
//
// CANCELLATION. Losing the last observer — an unmount, or a key change —
// cancels the in-flight request and fires the AbortSignal the catalog's query
// functions pass to Supabase. That is the other half of what the interim
// runtime's abort controller did, and it holds ONLY because the query function
// reads `context.signal`; see routeResourceQuery.js, where that is spelled out
// and tested.
//
// ---------------------------------------------------------------------------
// WHAT QUERY DOES NOT GIVE US, AND IS DONE BY HAND BELOW
//
//   A REFETCH THAT REFUSES TO RUN BEFORE IDENTITY SETTLES. In 5.101.4
//   `refetch()` fires even on a disabled query. The wrapper below makes it the
//   no-op it is today, and also stops React handing a click event straight into
//   Query's options argument at `onClick={loadAll}`.
//
// ---------------------------------------------------------------------------
// THE TWO PLACES THIS DIVERGES FROM THE INTERIM RUNTIME, RECORDED HONESTLY
//
//   * A FAILED SAME-KEY REFETCH KEEPS THE LAST GOOD DATA. The interim runtime
//     cleared it. Query does not, and Query is right for the same reason the
//     refetch fix is right: one failed refresh should not destroy the record
//     you are looking at. It is not observable on either pilot surface today —
//     both return their error screen before reading `data` — which is itself a
//     surface-level defect worth its own change, not one to smuggle in here.
//
//   * `client` IS NOT PART OF THE KEY. It was an effect dependency before, so
//     swapping clients refetched. A Supabase client is a transport, not a cache
//     dimension, and no caller in src/ passes one — the option exists for
//     tests. A swap now re-renders and takes effect on the next fetch.
// ============================================================================

import {useCallback, useMemo, useRef} from 'react';
import {useQuery} from '@tanstack/react-query';

import {useAuth} from '../../contexts/AuthContext.jsx';
import {sb} from '../../lib/supabase.js';
import {getOneShotResource, scopeOrNull} from './routeDataCatalog.js';
import type {ResourceParams} from './routeDataCatalog.js';
import {routeResourceQueryOptions, routeResourceResult} from './routeResourceQuery.js';
import type {RouteResourceProjection} from './routeResourceQuery.js';
// See routeResourceQuery.ts: the client type is owned by src/lib/supabaseTypes.ts
// because a second '@supabase/supabase-js' specifier anywhere in the repository
// breaks the single-owner static guard, even as an erased `import type`.
import type {SupabaseClient} from '../../lib/supabaseTypes.js';

export type UseRouteResourceOptions = {
  enabled?: boolean;
  client?: SupabaseClient;
};

/**
 * The payload is `unknown` because the catalog is keyed by a runtime string:
 * `getOneShotResource(id)` answers `OneShotRouteResource<unknown>`, so nothing at this
 * boundary knows which resource was asked for. Callers that want a concrete
 * shape narrow it themselves — the same position the .js was in, now stated.
 */
export type UseRouteResourceReturn = RouteResourceProjection<unknown> & {
  refetch: () => Promise<unknown>;
};

export function useRouteResource(
  resourceId: string,
  params?: ResourceParams,
  {enabled = true, client = sb}: UseRouteResourceOptions = {},
): UseRouteResourceReturn {
  const {authState, rolePreview} = useAuth();
  const resource = getOneShotResource(resourceId);
  const scope = scopeOrNull(authState, rolePreview);

  // Serialized deps, for the same reason the interim runtime used them: `scope`
  // and `params` are fresh objects every render, so comparing them by reference
  // would rebuild the key — and re-run everything downstream of it — forever.
  const options = useMemo(
    () => routeResourceQueryOptions({resource, scope, params, enabled, client}),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scope and params are compared by value through their serializations
    [resource, scope && JSON.stringify(scope), params && JSON.stringify(params), enabled, client],
  );

  const query = useQuery(options);

  // `ready` is the interim runtime's gate, unchanged in meaning: a scope exists
  // AND the caller has not disabled it. routeResourceQueryOptions has already
  // computed exactly that, so reading it back keeps one source of truth.
  const ready = options.enabled;

  // Latest-ref, so `refetch` keeps the permanently stable identity the interim
  // runtime's useCallback([]) gave it. Seven call sites hold it as `loadAll`,
  // two hand it straight to a control (`onClick={loadAll}`, `onRetry={loadAll}`)
  // and one is inside a deep event handler; an identity that churned every
  // render would be a new prop on every settle. Both refs are read only from an
  // event handler, never during render, so writing them here is safe.
  const readyRef = useRef(ready);
  readyRef.current = ready;
  const refetchRef = useRef<(() => Promise<unknown>) | null>(null);
  refetchRef.current = query.refetch;

  const refetch = useCallback(() => {
    // Takes no arguments and forwards none. `onClick={loadAll}` would otherwise
    // hand React's synthetic event to Query as its refetch options object.
    //
    // Before identity settles this resolves without fetching, which is what
    // happens today — the interim runtime's effect returned early and simply
    // settled its waiters. Query would instead run the query function, under
    // the inert key, with no scope.
    if (!readyRef.current) return Promise.resolve();
    // Non-null assertion rather than a guard, DELIBERATELY. `refetchRef.current`
    // is assigned from `query.refetch` on every render, above, and this callback
    // is only ever reached from an event handler — so by the time it can run,
    // the ref has been written at least once. Adding a `?? Promise.resolve()`
    // fallback would look safer while silently swallowing a genuine "refetch was
    // never wired" bug into a resolved promise, which is the failure mode this
    // module already exists to prevent elsewhere.
    return refetchRef.current!();
  }, []);

  // The {data, loading, error} projection lives in routeResourceResult, which
  // is pure and therefore actually testable in this repo's renderer-less vitest
  // environment. Every rule about what `loading` means — including the one that
  // keeps a same-key refetch from blanking the page — is proved there against
  // real QueryObserver results, not asserted about the text of this file.
  const {data, loading, error} = routeResourceResult(query, ready);

  return {data, loading, error, refetch};
}
